/**
 * The model behind the sign-in and enquiry pages' left panel: a child drawn
 * the way Kalqy's camera actually keeps one — seventeen points and the bones
 * between them — playing a round of the real game ("touch the number 3").
 *
 * 🔴 PURE AND FRAME-RATE FREE. `stepStage` advances by `dt` seconds and
 * `poseOf` turns the state into points; the canvas only draws. That keeps the
 * figure testable and lets the panel pause (hidden tab, reduced motion) without
 * the simulation drifting.
 *
 * ⚠️ "LEFT" HERE IS SCREEN-LEFT, NOT THE CHILD'S LEFT. The figure faces the
 * reader, and every rule below (which hand reaches, which way an elbow bends)
 * is written in screen terms.
 */

export interface Vec {
  x: number
  y: number
}

/** The seventeen COCO keypoints — what a pose model returns, and all Kalqy keeps. */
export const KEYPOINTS = [
  'nose',
  'leftEye',
  'rightEye',
  'leftEar',
  'rightEar',
  'leftShoulder',
  'rightShoulder',
  'leftElbow',
  'rightElbow',
  'leftWrist',
  'rightWrist',
  'leftHip',
  'rightHip',
  'leftKnee',
  'rightKnee',
  'leftAnkle',
  'rightAnkle',
] as const

export type Keypoint = (typeof KEYPOINTS)[number]
export type Pose = Record<Keypoint, Vec>

export const BONES: readonly (readonly [Keypoint, Keypoint])[] = [
  ['leftShoulder', 'rightShoulder'],
  ['leftShoulder', 'leftElbow'],
  ['leftElbow', 'leftWrist'],
  ['rightShoulder', 'rightElbow'],
  ['rightElbow', 'rightWrist'],
  ['leftShoulder', 'leftHip'],
  ['rightShoulder', 'rightHip'],
  ['leftHip', 'rightHip'],
  ['leftHip', 'leftKnee'],
  ['leftKnee', 'leftAnkle'],
  ['rightHip', 'rightKnee'],
  ['rightKnee', 'rightAnkle'],
  ['nose', 'leftEye'],
  ['nose', 'rightEye'],
  ['leftEye', 'leftEar'],
  ['rightEye', 'rightEar'],
]

/* ── Geometry ─────────────────────────────────────────────────────────────── */

export function dist(a: Vec, b: Vec): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v))
}

/** Frame-rate independent easing toward a target: `rate` is per second. */
export function approach(from: number, to: number, rate: number, dt: number): number {
  return to + (from - to) * Math.exp(-rate * dt)
}

/**
 * Two-bone IK in the plane: the middle joint (elbow, knee) and the end
 * (wrist, ankle) for a limb rooted at `root` reaching for `target`.
 *
 * Out of reach, the limb points straight at the target at full length. Of the
 * two bends that fit, it takes the one whose middle joint sits further toward
 * `outward` (−1 screen-left, +1 screen-right) — elbows and knees bend away
 * from the body, never through it.
 */
export function solveLimb(root: Vec, target: Vec, upper: number, lower: number, outward: -1 | 1): { mid: Vec; end: Vec } {
  const dx = target.x - root.x
  const dy = target.y - root.y
  const raw = Math.hypot(dx, dy)
  const ux = raw > 1e-6 ? dx / raw : 0
  const uy = raw > 1e-6 ? dy / raw : 1
  const d = clamp(raw, Math.abs(upper - lower) + 1e-3, upper + lower - 1e-3)
  const end = { x: root.x + ux * d, y: root.y + uy * d }
  const a = (upper * upper - lower * lower + d * d) / (2 * d)
  const h = Math.sqrt(Math.max(0, upper * upper - a * a))
  const bx = root.x + ux * a
  const by = root.y + uy * a
  const one = { x: bx - uy * h, y: by + ux * h }
  const two = { x: bx + uy * h, y: by - ux * h }
  const mid = (one.x - two.x) * outward >= 0 ? one : two
  return { mid, end }
}

/* ── Layout ───────────────────────────────────────────────────────────────── */

export interface StageLayout {
  w: number
  h: number
  /** Where the feet stand. */
  ground: number
  /** The figure's height, feet to crown. */
  size: number
  bubbleR: number
}

/** Body proportions as fractions of `size` — a young child: big head, short legs. */
export const BODY = {
  thigh: 0.235,
  shin: 0.225,
  hipHeight: 0.44,
  torso: 0.3,
  shoulderHalf: 0.135,
  hipHalf: 0.085,
  upperArm: 0.185,
  forearm: 0.175,
  neck: 0.15,
  jump: 0.3,
} as const

export function layoutFor(w: number, h: number): StageLayout {
  const size = clamp(h * 0.56, 140, 330)
  return {
    w,
    h,
    ground: h - Math.max(14, h * 0.035),
    size,
    bubbleR: clamp(size * 0.105, 19, 32),
  }
}

/** The band bubbles float in: high enough to need a reach, low enough to touch with a jump. */
export function bubbleArea(l: StageLayout): { x0: number; x1: number; y0: number; y1: number } {
  const pad = l.bubbleR + 10
  return {
    x0: pad,
    x1: Math.max(pad, l.w - pad),
    y0: Math.max(pad, l.ground - l.size * 1.36),
    y1: Math.max(pad, l.ground - l.size * 0.66),
  }
}

/* ── The round ────────────────────────────────────────────────────────────── */

export const BUBBLE_HUES = ['#e5007a', '#00a9a2', '#f5b301', '#2e90d9', '#8b4bd8', '#45b308'] as const

export interface Bubble {
  id: number
  label: string
  hue: string
  /** Rest position; the bubble bobs around it. */
  x: number
  y: number
  phase: number
  /** Seconds left of the "not that one" shake. */
  wobble: number
  popped: boolean
}

export interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  hue: string
  size: number
}

const NUMBERS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const
const LETTERS = ['A', 'B', 'C', 'D', 'E', 'K', 'M', 'S'] as const

export type Rand = () => number

/** Distinct picks from `pool`, in random order. */
function pick<T>(pool: readonly T[], n: number, rand: Rand): T[] {
  const rest = [...pool]
  const out: T[] = []
  while (out.length < n && rest.length > 0) {
    const i = Math.floor(rand() * rest.length) % rest.length
    out.push(rest.splice(i, 1)[0] as T)
  }
  return out
}

/**
 * A fresh round: `count` bubbles that do not overlap, alternating numbers and
 * letters by round, one of them the goal. Positions are rejection-sampled;
 * on a stage too small to fit them apart, the last tries are kept anyway.
 */
export function makeRound(l: StageLayout, round: number, rand: Rand, count = 4): { goal: string; bubbles: Bubble[] } {
  const pool = round % 2 === 0 ? NUMBERS : LETTERS
  const labels = pick(pool, count, rand)
  const hues = pick(BUBBLE_HUES, count, rand)
  const area = bubbleArea(l)
  const gap = l.bubbleR * 2.5
  const bubbles: Bubble[] = []
  for (let i = 0; i < labels.length; i++) {
    let x = 0
    let y = 0
    for (let tries = 0; tries < 40; tries++) {
      x = area.x0 + rand() * (area.x1 - area.x0)
      y = area.y0 + rand() * (area.y1 - area.y0)
      if (bubbles.every((b) => Math.hypot(b.x - x, b.y - y) >= gap)) break
    }
    bubbles.push({
      id: round * 10 + i,
      label: labels[i] as string,
      hue: hues[i % hues.length] ?? BUBBLE_HUES[0],
      x,
      y,
      phase: rand() * Math.PI * 2,
      wobble: 0,
      popped: false,
    })
  }
  const goal = labels[Math.floor(rand() * labels.length) % labels.length] ?? labels[0] ?? '1'
  return { goal, bubbles }
}

/** Where a bubble is drawn at time `t`: a slow bob around its rest point, plus any shake. */
export function bubbleAt(b: Bubble, t: number): Vec {
  const shake = b.wobble > 0 ? Math.sin(b.wobble * 46) * 6 * (b.wobble / 0.5) : 0
  return {
    x: b.x + Math.cos(t * 0.9 + b.phase) * 5 + shake,
    y: b.y + Math.sin(t * 1.3 + b.phase) * 7,
  }
}

/* ── The stage ────────────────────────────────────────────────────────────── */

export interface StageState {
  t: number
  layout: StageLayout
  round: number
  goal: string
  bubbles: Bubble[]
  particles: Particle[]
  score: number
  /** Hip-centre x and its velocity. */
  x: number
  vx: number
  /** Walk-cycle phase, advanced by distance walked. */
  walk: number
  /** Seconds into a jump, or −1 on the ground. */
  jump: number
  /** Seconds left of the both-arms-up cheer after a correct touch. */
  cheer: number
  /** Seconds until the next round starts (after a pop), or −1. */
  next: number
  /** Where the reaching hand is heading, eased. */
  hand: Vec
  /** −1 the screen-left hand reaches, +1 the screen-right. */
  side: -1 | 1
  /** Recent positions of the reaching wrist, newest last. */
  trail: Vec[]
}

export type StageEvent = { kind: 'pop'; label: string } | { kind: 'miss'; label: string } | { kind: 'round'; goal: string }

export interface StageInput {
  /** The reader's pointer in stage coordinates, while they are steering. */
  pointer: Vec | null
}

export function createStage(w: number, h: number, rand: Rand): StageState {
  const layout = layoutFor(w, h)
  const { goal, bubbles } = makeRound(layout, 0, rand)
  const x = w / 2
  return {
    t: 0,
    layout,
    round: 0,
    goal,
    bubbles,
    particles: [],
    score: 0,
    x,
    vx: 0,
    walk: 0,
    jump: -1,
    cheer: 0,
    next: -1,
    hand: { x: x + layout.size * 0.2, y: layout.ground - layout.size * 0.5 },
    side: 1,
    trail: [],
  }
}

/**
 * Re-fit a running stage to a new size: positions scale with the box, so a
 * resize keeps the round, the score and where everything is relative to it.
 */
export function resizeStage(s: StageState, w: number, h: number): void {
  const sx = w / Math.max(1, s.layout.w)
  const sy = h / Math.max(1, s.layout.h)
  s.layout = layoutFor(w, h)
  s.x *= sx
  s.hand = { x: s.hand.x * sx, y: s.hand.y * sy }
  s.trail = []
  for (const b of s.bubbles) {
    b.x *= sx
    b.y *= sy
  }
}

const JUMP_TIME = 0.62
const CROUCH_TIME = 0.1

/** The vertical offsets of a jump at `t` seconds in: a dip, then an arc. */
export function jumpOffsets(t: number, size: number): { crouch: number; lift: number } {
  if (t < 0) return { crouch: 0, lift: 0 }
  if (t < CROUCH_TIME) return { crouch: size * 0.05 * Math.sin((t / CROUCH_TIME) * (Math.PI / 2)), lift: 0 }
  const u = clamp((t - CROUCH_TIME) / (JUMP_TIME - CROUCH_TIME), 0, 1)
  return { crouch: size * 0.05 * (1 - u) * (1 - u), lift: 4 * BODY.jump * size * u * (1 - u) }
}

/** The bubble the figure plays for when nobody is steering. */
function autopilotTarget(s: StageState): Vec | null {
  const goal = s.bubbles.find((b) => b.label === s.goal && !b.popped)
  return goal ? bubbleAt(goal, s.t) : null
}

/**
 * Advance the stage by `dt` seconds. Returns what happened, so the panel can
 * update its prompt and score without reading the state every frame.
 */
export function stepStage(s: StageState, dt: number, input: StageInput, rand: Rand): StageEvent[] {
  const events: StageEvent[] = []
  const l = s.layout
  const size = l.size
  s.t += dt

  // The next round, once the cheer has played.
  if (s.next >= 0) {
    s.next -= dt
    if (s.next < 0) {
      s.round += 1
      const r = makeRound(l, s.round, rand)
      s.goal = r.goal
      s.bubbles = r.bubbles
      events.push({ kind: 'round', goal: r.goal })
    }
  }

  // Where the hand wants to be: the reader's pointer, or the goal bubble.
  const steering = input.pointer !== null
  const auto = autopilotTarget(s)
  const want = input.pointer ?? auto ?? { x: s.x + s.side * size * 0.25, y: l.ground - size * 0.55 }
  s.hand = {
    x: approach(s.hand.x, want.x, steering ? 16 : 4.5, dt),
    y: approach(s.hand.y, want.y, steering ? 16 : 4.5, dt),
  }

  // Which hand: whichever side of the body the target is on, with a dead zone so it does not flicker.
  const rel = s.hand.x - s.x
  if (Math.abs(rel) > size * 0.06) s.side = rel < 0 ? -1 : 1

  // Walk so the reaching shoulder ends up under the hand, not the hips.
  const standAt = clamp(s.hand.x - s.side * size * 0.2, size * 0.3, l.w - size * 0.3)
  const gap = standAt - s.x
  const maxSpeed = size * 1.7
  const desired = Math.abs(gap) < size * 0.04 ? 0 : clamp(gap * 5, -maxSpeed, maxSpeed)
  s.vx = approach(s.vx, desired, 10, dt)
  s.x += s.vx * dt
  s.walk += (Math.abs(s.vx) * dt) / (size * 0.32)

  // Jump when the hand is above what a standing reach can touch.
  const shoulderY = l.ground - size * (BODY.hipHeight + BODY.torso)
  const reach = size * (BODY.upperArm + BODY.forearm)
  if (s.jump >= 0) {
    s.jump += dt
    if (s.jump > JUMP_TIME) s.jump = -1
  } else if (s.hand.y < shoulderY - reach * 0.82 && Math.abs(gap) < size * 0.35) {
    s.jump = 0
  }

  if (s.cheer > 0) s.cheer = Math.max(0, s.cheer - dt)

  // Touches: the reaching wrist against each bubble.
  const pose = poseOf(s)
  const wrist = s.side < 0 ? pose.leftWrist : pose.rightWrist
  s.trail.push(wrist)
  if (s.trail.length > 14) s.trail.shift()

  for (const b of s.bubbles) {
    if (b.wobble > 0) b.wobble = Math.max(0, b.wobble - dt)
    if (b.popped || b.wobble > 0 || s.next >= 0) continue
    const at = bubbleAt(b, s.t)
    if (dist(at, wrist) > l.bubbleR + size * 0.035) continue
    if (b.label === s.goal) {
      b.popped = true
      s.score += 1
      s.cheer = 0.8
      s.next = 0.95
      burst(s, at, b.hue, rand)
      events.push({ kind: 'pop', label: b.label })
    } else {
      b.wobble = 0.5
      events.push({ kind: 'miss', label: b.label })
    }
  }

  // Confetti: gravity, drag, fade.
  for (const p of s.particles) {
    p.vy += size * 2.4 * dt
    p.vx *= Math.exp(-1.5 * dt)
    p.x += p.vx * dt
    p.y += p.vy * dt
    p.life -= dt
  }
  s.particles = s.particles.filter((p) => p.life > 0)

  return events
}

function burst(s: StageState, at: Vec, hue: string, rand: Rand): void {
  const size = s.layout.size
  for (let i = 0; i < 22; i++) {
    const a = rand() * Math.PI * 2
    const v = size * (0.6 + rand() * 1.4)
    s.particles.push({
      x: at.x,
      y: at.y,
      vx: Math.cos(a) * v,
      vy: Math.sin(a) * v - size * 0.9,
      life: 0.7 + rand() * 0.5,
      hue: i % 3 === 0 ? '#ffffff' : i % 3 === 1 ? hue : BUBBLE_HUES[i % BUBBLE_HUES.length] ?? hue,
      size: 2 + rand() * 3.5,
    })
  }
}

/* ── The figure ───────────────────────────────────────────────────────────── */

/** The seventeen points for the stage's current state. */
export function poseOf(s: StageState): Pose {
  const l = s.layout
  const size = l.size
  const B = BODY
  const { crouch, lift } = jumpOffsets(s.jump, size)
  const airborne = s.jump >= CROUCH_TIME
  const breathe = Math.sin(s.t * 2.1) * size * 0.006
  const bob = airborne ? 0 : Math.abs(Math.sin(s.walk * Math.PI)) * size * 0.012 * Math.min(1, Math.abs(s.vx) / size)

  // Hips and shoulders.
  const hipY = l.ground - size * B.hipHeight + crouch - lift - bob
  const lean = clamp(s.vx * 0.035, -size * 0.06, size * 0.06) + clamp((s.hand.x - s.x) * 0.06, -size * 0.04, size * 0.04)
  const hip: Vec = { x: s.x, y: hipY }
  const chest: Vec = { x: s.x + lean, y: hipY - size * B.torso + breathe }
  const leftShoulder = { x: chest.x - size * B.shoulderHalf, y: chest.y + (s.side < 0 ? -size * 0.012 : 0) }
  const rightShoulder = { x: chest.x + size * B.shoulderHalf, y: chest.y + (s.side > 0 ? -size * 0.012 : 0) }
  const leftHip = { x: hip.x - size * B.hipHalf, y: hip.y }
  const rightHip = { x: hip.x + size * B.hipHalf, y: hip.y }

  // Arms: the reaching one goes for the hand target; the other relaxes, or both go up in a cheer.
  const cheering = s.cheer > 0
  const arm = (shoulder: Vec, side: -1 | 1): { mid: Vec; end: Vec } => {
    let target: Vec
    if (cheering) {
      const wave = Math.sin(s.t * 14) * size * 0.03
      target = { x: shoulder.x + side * size * 0.2 + wave, y: shoulder.y - size * 0.32 }
    } else if (side === s.side) {
      target = s.hand
    } else {
      const sway = Math.sin(s.walk * Math.PI + (side > 0 ? 0 : Math.PI)) * size * 0.05
      target = { x: shoulder.x + side * size * 0.13 + sway, y: shoulder.y + size * 0.3 }
    }
    return solveLimb(shoulder, target, size * B.upperArm, size * B.forearm, side)
  }
  const leftArm = arm(leftShoulder, -1)
  const rightArm = arm(rightShoulder, 1)

  // Legs: planted and stepping on the ground, tucked in the air.
  const leg = (hipPt: Vec, side: -1 | 1): { mid: Vec; end: Vec } => {
    let foot: Vec
    if (airborne) {
      foot = { x: hipPt.x + side * size * 0.04, y: hipPt.y + size * 0.36 }
    } else {
      const phase = s.walk * Math.PI + (side > 0 ? Math.PI : 0)
      const moving = Math.min(1, Math.abs(s.vx) / (size * 0.6))
      foot = {
        x: hipPt.x + side * size * 0.03 + Math.cos(phase) * size * 0.07 * moving,
        y: l.ground - Math.max(0, Math.sin(phase)) * size * 0.06 * moving,
      }
    }
    return solveLimb(hipPt, foot, size * B.thigh, size * B.shin, side)
  }
  const leftLeg = leg(leftHip, -1)
  const rightLeg = leg(rightHip, 1)

  // Head: tilts a little toward what it is reaching for.
  const look = clamp((s.hand.x - chest.x) * 0.05, -size * 0.025, size * 0.025)
  const nose = { x: chest.x + look, y: chest.y - size * B.neck }
  const eyeY = nose.y - size * 0.034
  return {
    nose,
    leftEye: { x: nose.x - size * 0.03, y: eyeY },
    rightEye: { x: nose.x + size * 0.03, y: eyeY },
    leftEar: { x: nose.x - size * 0.068, y: eyeY + size * 0.012 },
    rightEar: { x: nose.x + size * 0.068, y: eyeY + size * 0.012 },
    leftShoulder,
    rightShoulder,
    leftElbow: leftArm.mid,
    rightElbow: rightArm.mid,
    leftWrist: leftArm.end,
    rightWrist: rightArm.end,
    leftHip,
    rightHip,
    leftKnee: leftLeg.mid,
    rightKnee: rightLeg.mid,
    leftAnkle: leftLeg.end,
    rightAnkle: rightLeg.end,
  }
}

/** The figure's bounding box, padded — the detector's "child" box in the camera view. */
export function poseBox(p: Pose, pad: number): { x: number; y: number; w: number; h: number } {
  let x0 = Infinity
  let y0 = Infinity
  let x1 = -Infinity
  let y1 = -Infinity
  for (const k of KEYPOINTS) {
    const v = p[k]
    x0 = Math.min(x0, v.x)
    y0 = Math.min(y0, v.y)
    x1 = Math.max(x1, v.x)
    y1 = Math.max(y1, v.y)
  }
  return { x: x0 - pad, y: y0 - pad * 1.6, w: x1 - x0 + pad * 2, h: y1 - y0 + pad * 2.6 }
}

/** A small seeded generator, so a test (or a screenshot) sees the same round every time. */
export function seeded(seed: number): Rand {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}
